import {RefObject, useEffect, useRef} from "react";

interface ScrollableModel {
    events: {
        on(event: "scrollIntoView", handler: () => void): () => void;
    };
}

export interface UseScrollIntoViewDeferredOptions {
    behavior?: ScrollBehavior;
    block?: ScrollLogicalPosition;
    inline?: ScrollLogicalPosition;
}

/**
 * Like `useScrollIntoView`, but runs `scrollIntoView` after the next frames so the DOM can commit
 * (e.g. content that appears on the same tick as `scrollIntoView` emit from the model).
 */
export function useScrollIntoViewDeferred<TElement extends HTMLElement = HTMLElement>(
    model: ScrollableModel,
    options?: UseScrollIntoViewDeferredOptions,
): RefObject<TElement | null> {
    const ref = useRef<TElement>(null);

    useEffect(() => {
        const unsubscribe = model.events.on("scrollIntoView", () => {
            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    ref.current?.scrollIntoView({
                        behavior: options?.behavior ?? "smooth",
                        block: options?.block ?? "center",
                        inline: options?.inline ?? "nearest",
                    });
                });
            });
        });

        return () => {
            unsubscribe();
        };
    }, [model, options]);

    return ref;
}
